import { Box, Tab } from "@mui/material";
import React, { useEffect, useState } from "react";
import { TabContext, TabList, TabPanel } from "@mui/lab";
import AllClient from "./clientsList/AllClient";
import InHouseClients from "./clientsList/InHouseClients";
import AftercareClients from "./clientsList/AftercareClients";
import InactiveClients from "./clientsList/InactiveClients";
import MiraclesAtHomeClients from "./clientsList/MiraclesAtHomeClients";
import IncomingClients from "./clientsList/IncomingClients";
import ClinicalAssessmentClient from "./clientsList/ClinicalAssessmentClient";
import ContinuedTherapy from "./clientsList/ContinuedTherapy";
import DeniedClients from "./clientsList/DeniedClients";
import PotentialClients from "./clientsList/PotentialClients";
import { useLocation } from "react-router-dom";
import DuplicateClients from "./clientsList/DuplicateClients";

const statusToTabValue = {
  "In house": "1",
  Inhouse: "1",
  inhouse: "1",
  Incoming: "2",
  incoming: "2",
  Potential: "3",
  potential: "3",
  "Clinical Assessment": "4",
  clinicalAssessment: "4",
  "clinical-assessment": "4",
  Aftercare: "5",
  aftercare: "5",
  "Miracles@home": "6",
  miraclesAtHome: "6",
  "miracles-at-home": "6",
  "Continued Therapy": "7",
  continuedTherapy: "7",
  "continued-therapy": "7",
  Inactive: "8",
  inactive: "8",
  "Denied Admission": "9",
  deniedAdmission: "9",
  "denied-admission": "9",
  All: "10",
  all: "10",
  Duplicate: "11",
  duplicate: "11",
};

function ClientsTab({
  viewMode,
  userRole = "",
  therapistFilter = null,
  rehabFilter = null,
  initialTab = null,
}) {
  const [value, setValue] = useState(statusToTabValue[initialTab] || "1");
  const isPhilippinesView = rehabFilter === "miracles_asia_philippines";
  const inHouseTabLabel = isPhilippinesView ? "Active" : "Inhouse";
  const incomingTabLabel = "Incoming";
  const handleChange = (event, newValue) => {
    setValue(newValue);
  };

  const location = useLocation();
  const isExternal = userRole === "External";

  // Compute available tab values based on current userRole
  const getAvailableTabs = (role) => {
    if (role === "External") return ["10"]; // All
    const anyRole = [
      "Super Admin",
      "Admin",
      "Therapist",
      "Admission staff",
      "Support Staff",
      "Psycho-ed",
      "Finance team",
      "Medical Staff",
      "Hospitality Staff",
      "Clinical Director",
    ];
    const hasAny = (r) => anyRole.includes(r);
    const arr = [];
    if (hasAny(role)) arr.push("1"); // Inhouse
    arr.push("2"); // Incoming (always shown)
    if (role !== "Hospitality Staff") arr.push("4"); // Clinical Assessment
    if (hasAny(role)) arr.push("5"); // Aftercare
    if (hasAny(role)) arr.push("6"); // Miracles@home
    if (hasAny(role)) arr.push("7"); // Continued Therapy
    if (role !== "Hospitality Staff") arr.push("8"); // Inactive
    if (hasAny(role)) arr.push("9"); // Denied Admission
    if (hasAny(role)) arr.push("10"); // All
    if (hasAny(role)) arr.push("11"); // Duplicate
    return arr;
  };

  // Ensure selected value matches an available tab when role loads/changes
  useEffect(() => {
    const available = getAvailableTabs(userRole);
    if (!available.includes(value)) {
      // keep the current preference in state; UI will show a safe value
      // optionally, you can uncomment to force-select the first available tab
      // setValue(available[0] || value);
    }
  }, [userRole, value]);

  // Derive a safe value for rendering to avoid MUI warning when role isn't loaded yet
  const availableForRender = getAvailableTabs(userRole);
  const displayedValue = availableForRender.includes(value)
    ? value
    : availableForRender[0] || value;

  useEffect(() => {
    // Si l'état est passé avec un statut client, sélectionnez l'onglet correspondant
    if (location.state?.selectedTab) {
      const tabValue = statusToTabValue[location.state.selectedTab];
      if (tabValue) {
        setValue(tabValue);
        return;
      }
    }

    const tabValue = statusToTabValue[initialTab];
    if (tabValue) {
      setValue(tabValue);
    }
  }, [location, initialTab]);

  return (
    <div className="tab-client-container">
      <Box>
        <TabContext value={displayedValue}>
          <Box
            sx={{
              borderBottom: 1,
              borderColor: "divider",
              width: "auto",
              marginLeft: "60px",
            }}
          >
            <TabList
              aria-label="Tabs example"
              onChange={handleChange}
              variant="scrollable"
              scrollButtons="auto"
            >
              {(isExternal
                ? [<Tab key="10" label="All" value="10" />]
                : [
                    (userRole === "Super Admin" ||
                      userRole === "Admin" ||
                      userRole === "Therapist" ||
                      userRole === "Admission staff" ||
                      userRole === "Support Staff" ||
                      userRole === "Psycho-ed" ||
                      userRole === "Finance team" ||
                      userRole === "Medical Staff" ||
                      userRole === "Hospitality Staff" ||
                      userRole === "Clinical Director") && (
                      <Tab key="1" label={inHouseTabLabel} value="1" />
                    ),
                    <Tab key="2" label={incomingTabLabel} value="2" />,
                    userRole !== "Hospitality Staff" && (
                      <Tab key="4" label="Clinical Assessment" value="4" />
                    ),
                    <Tab key="5" label="Aftercare" value="5" />,
                    (userRole === "Admin" ||
                      userRole === "Therapist" ||
                      userRole === "Super Admin" ||
                      userRole === "Admission staff" ||
                      userRole === "Support Staff" ||
                      userRole === "Psycho-ed" ||
                      userRole === "Finance team" ||
                      userRole === "Medical Staff" ||
                      userRole === "Hospitality Staff" ||
                      userRole === "Clinical Director") && (
                      <Tab key="6" label="Miracles@home" value="6" />
                    ),
                    (userRole === "Admin" ||
                      userRole === "Therapist" ||
                      userRole === "Super Admin" ||
                      userRole === "Admission staff" ||
                      userRole === "Support Staff" ||
                      userRole === "Psycho-ed" ||
                      userRole === "Finance team" ||
                      userRole === "Medical Staff" ||
                      userRole === "Hospitality Staff" ||
                      userRole === "Clinical Director") && (
                      <Tab key="7" label="Continued Therapy" value="7" />
                    ),
                    (userRole === "Admin" ||
                      userRole === "Super Admin" ||
                      userRole === "Admission staff" ||
                      userRole === "Support Staff" ||
                      userRole === "Psycho-ed" ||
                      userRole === "Finance team" ||
                      userRole === "Medical Staff" ||
                      userRole === "Therapist" ||
                      userRole === "Hospitality Staff" ||
                      userRole === "Clinical Director") && (
                      <Tab key="8" label="Inactive" value="8" />
                    ),
                    (userRole === "Admin" ||
                      userRole === "Super Admin" ||
                      userRole === "Admission staff" ||
                      userRole === "Support Staff" ||
                      userRole === "Psycho-ed" ||
                      userRole === "Finance team" ||
                      userRole === "Medical Staff" ||
                      userRole === "Therapist" ||
                      userRole === "Hospitality Staff" ||
                      userRole === "Clinical Director") && (
                      <Tab key="9" label="Denied Admission" value="9" />
                    ),
                    (userRole === "Admin" ||
                      userRole === "Super Admin" ||
                      userRole === "Admission staff" ||
                      userRole === "Support Staff" ||
                      userRole === "Psycho-ed" ||
                      userRole === "Finance team" ||
                      userRole === "Medical Staff" ||
                      userRole === "Therapist" ||
                      userRole === "Hospitality Staff" ||
                      userRole === "Clinical Director") && (
                      <Tab key="10" label="All" value="10" />
                    ),
                    (userRole === "Admin" ||
                      userRole === "Super Admin" ||
                      userRole === "Admission staff" ||
                      userRole === "Support Staff" ||
                      userRole === "Psycho-ed" ||
                      userRole === "Finance team" ||
                      userRole === "Medical Staff" ||
                      userRole === "Therapist" ||
                      userRole === "Hospitality Staff" ||
                      userRole === "Clinical Director") && (
                      <Tab key="11" label="Duplicate" value="11" />
                    ),
                  ]
              ).filter(Boolean)}
            </TabList>
          </Box>
          {(() => {
            switch (displayedValue) {
              case "1":
                return (
                  <TabPanel value="1">
                    <InHouseClients
                      viewMode={viewMode}
                      therapistFilter={therapistFilter}
                      rehabFilter={rehabFilter}
                    />
                  </TabPanel>
                );

              case "2":
                return (
                  <TabPanel value="2">
                    <IncomingClients
                      viewMode={viewMode}
                      therapistFilter={therapistFilter}
                      rehabFilter={rehabFilter}
                    />
                  </TabPanel>
                );

              case "3":
                return (
                  <TabPanel value="3">
                    <PotentialClients
                      viewMode={viewMode}
                      therapistFilter={therapistFilter}
                      rehabFilter={rehabFilter}
                    />
                  </TabPanel>
                );

              case "4":
                return (
                  <TabPanel value="4">
                    <ClinicalAssessmentClient
                      viewMode={viewMode}
                      therapistFilter={therapistFilter}
                      rehabFilter={rehabFilter}
                    />
                  </TabPanel>
                );
              case "5":
                return (
                  <TabPanel value="5">
                    <AftercareClients
                      viewMode={viewMode}
                      therapistFilter={therapistFilter}
                      rehabFilter={rehabFilter}
                    />
                  </TabPanel>
                );
              case "6":
                return (
                  <TabPanel value="6">
                    <MiraclesAtHomeClients
                      viewMode={viewMode}
                      therapistFilter={therapistFilter}
                      rehabFilter={rehabFilter}
                    />
                  </TabPanel>
                );
              case "7":
                return (
                  <TabPanel value="7">
                    <ContinuedTherapy
                      viewMode={viewMode}
                      therapistFilter={therapistFilter}
                      rehabFilter={rehabFilter}
                    />
                  </TabPanel>
                );
              case "8":
                return (
                  <TabPanel value="8">
                    <InactiveClients
                      viewMode={viewMode}
                      therapistFilter={therapistFilter}
                      rehabFilter={rehabFilter}
                    />
                  </TabPanel>
                );

              case "9":
                return (
                  <TabPanel value="9">
                    <DeniedClients
                      viewMode={viewMode}
                      therapistFilter={therapistFilter}
                      rehabFilter={rehabFilter}
                    />
                  </TabPanel>
                );
              case "10":
                return (
                  <TabPanel value="10">
                    <AllClient
                      viewMode={viewMode}
                      therapistFilter={therapistFilter}
                      rehabFilter={rehabFilter}
                    />
                  </TabPanel>
                );

              case "11":
                return (
                  <TabPanel value="11">
                    <DuplicateClients
                      viewMode={viewMode}
                      therapistFilter={therapistFilter}
                      rehabFilter={rehabFilter}
                    />
                  </TabPanel>
                );
              default:
                return null;
            }
          })()}
        </TabContext>
      </Box>
    </div>
  );
}

export default ClientsTab;
